/**
 * Map, group, reorder, and filter dashboard KPI cards.
 */

import { arrayMove } from '@dnd-kit/sortable';
import {
  COLLAPSED_KPI_CARD_COUNT,
  KPI_CARD_COLOR,
  KPI_FILTER_ELEMENT_IDS,
  UNGROUPED_KPI_LABEL,
} from './kpi-configs';
import { getLucideIcon } from '@/components/dashboard/dashboard-utils';
import { dashboardHasElement } from '@/utils/dashboard-ie-utils';
import { parseLocalDateString, toLocalDateString } from '@/utils/format-date-utils';
import type {
  DashboardComparisonStat,
  DashboardStat,
  DashboardStatsResponse,
  StatTrend,
} from '@/types/dashboard.types';
import type { InterfaceElement } from '@/types/interface-elements.types';
import type {
  DashboardKpiEntry,
  KpiCardData,
  KpiFilterVisibility,
  KpiGroup,
  KpiLoadStatus,
} from '@/types/kpi.types';
import type {
  ComparisonPeriod,
  DashboardStatsDateFilterState,
  DashboardStatsPeriod,
} from '@/utils/dashboard-stats-date-range-utils';

/** Inputs for {@link buildKpiCards}. */
export interface BuildKpiCardsParams {
  elements: InterfaceElement[];
  response: DashboardStatsResponse | undefined;
  comparisonPeriod: ComparisonPeriod;
}

/** Inputs for {@link resolveKpiLoadStatus}. */
export interface ResolveKpiLoadStatusParams {
  isKpiError: boolean;
  isLoadingElements: boolean;
  isLoadingKpis: boolean;
  response: DashboardStatsResponse | undefined;
}

/** Sort comparator: visible cards before hidden ones. */
export function compareVisibleFirst(a: KpiCardData, b: KpiCardData): number {
  if (a.visible === b.visible) return 0;
  if (a.visible) return -1;
  return 1;
}

/** Visible cards in view mode; all cards with hidden ones last in edit mode. */
export function orderKpiCards(items: KpiCardData[], editMode: boolean): KpiCardData[] {
  if (!editMode) return items.filter((kpi) => kpi.visible);
  return [...items].sort(compareVisibleFirst);
}

/** True when at least one card carries a KPI family. */
export function isGroupedKpis(items: KpiCardData[]): boolean {
  return items.some((kpi) => !!kpi.groupLabel);
}

/** Hidden-card count shown in the section title while editing. */
export function countHiddenKpis(items: KpiCardData[]): number {
  return items.filter((kpi) => !kpi.visible).length;
}

/** Flip `visible` on the matching KPI card. */
export function toggleKpiVisibility(items: KpiCardData[], id: string): KpiCardData[] {
  return items.map((item) => (item.id === id ? { ...item, visible: !item.visible } : item));
}

/** Persist order and visibility in the shape `saveConfig` accepts. */
export function buildKpiPrefsPayload(items: KpiCardData[]): Record<string, unknown> {
  return {
    elements: items.map((item, index) => ({
      id: item.id,
      visible: item.visible,
      order: index,
    })),
  };
}

/** KPI value from the dashboard API, else the IE config fallback. */
function resolveMappedKpiValue(
  row: DashboardKpiEntry | undefined,
  configValue: unknown,
): string | number {
  if (row && 'value' in row && row.value != null) return row.value;
  if (row && 'currentValue' in row) return (row as DashboardComparisonStat).currentValue;
  return (configValue as string) ?? '0';
}

/** Trend only when a comparison period is selected. */
function resolveMappedTrend(
  row: DashboardKpiEntry | undefined,
  configTrend: unknown,
  comparisonPeriod: ComparisonPeriod,
): StatTrend | undefined {
  if (comparisonPeriod === 'none') return undefined;
  if (row && 'trend' in row) return (row as DashboardStat).trend;
  return configTrend as StatTrend | undefined;
}

/** Description from the dashboard API, else the IE config. */
function resolveMappedDescription(
  row: DashboardKpiEntry | undefined,
  configDescription: unknown,
): string | undefined {
  if (row && 'description' in row && row.description) return row.description;
  return configDescription as string | undefined;
}

/** Comparison label only when a comparison period is selected. */
function resolveMappedComparisonLabel(
  row: DashboardKpiEntry | undefined,
  comparisonPeriod: ComparisonPeriod,
): string | undefined {
  if (comparisonPeriod === 'none') return undefined;
  if (row && 'comparisonLabel' in row) return row.comparisonLabel;
  return undefined;
}

/** Sort IE rows: user drag-order wins; grouped dashboards fall back to `groupOrder`. */
function compareKpiElements(
  a: InterfaceElement,
  b: InterfaceElement,
  hasFamilies: boolean,
): number {
  const orderA = a.userOrder ?? (hasFamilies ? a.groupOrder : a.order) ?? Infinity;
  const orderB = b.userOrder ?? (hasFamilies ? b.groupOrder : b.order) ?? Infinity;
  return orderA - orderB;
}

/** Map one `stat_card` IE row plus its API value onto a KPI card. */
function mapKpiElementToCard(
  element: InterfaceElement,
  response: DashboardStatsResponse | undefined,
  comparisonPeriod: ComparisonPeriod,
): KpiCardData {
  const apiRow = response?.stats?.[element.elementId];
  return {
    id: element.elementId,
    title: element.elementLabel,
    value: resolveMappedKpiValue(apiRow, element.config.value),
    icon: getLucideIcon((element.config.icon as string) || 'HelpCircle'),
    iconColor: KPI_CARD_COLOR.icon,
    iconBgColor: KPI_CARD_COLOR.bg,
    titleColor: KPI_CARD_COLOR.title,
    trend: resolveMappedTrend(apiRow, element.config.trend, comparisonPeriod),
    description: resolveMappedDescription(apiRow, element.config.description),
    tooltip: element.config.tooltip as string | undefined,
    comparisonLabel: resolveMappedComparisonLabel(apiRow, comparisonPeriod),
    visible: element.visible !== false,
    groupLabel: element.groupLabel,
    groupIcon: element.groupIcon,
    groupOrder: element.groupOrder,
  };
}

/**
 * KPI cards from dashboard IE. Grouped dashboards use seeder `groupOrder` per family;
 * user drag-order wins. Ungrouped keeps original sort so leftover in-person role order
 * does not mix families on remote.
 */
export function buildKpiCards(params: BuildKpiCardsParams): KpiCardData[] {
  const kpiElements = params.elements.filter((el) => el.elementType === 'stat_card');
  const hasFamilies = kpiElements.some((el) => !!el.groupLabel);
  return kpiElements
    .slice()
    .sort((a, b) => compareKpiElements(a, b, hasFamilies))
    .map((el) => mapKpiElementToCard(el, params.response, params.comparisonPeriod));
}

/** Bucket cards by family; ungrouped dashboards stay a single flat group. */
export function groupKpiCards(orderedItems: KpiCardData[], grouped: boolean): KpiGroup[] {
  if (!grouped) return [{ label: '', icon: null, cards: orderedItems }];

  const byLabel = new Map<string, KpiGroup>();
  for (const card of orderedItems) {
    const label = card.groupLabel || UNGROUPED_KPI_LABEL;
    let group = byLabel.get(label);
    if (!group) {
      group = { label, icon: card.groupIcon ? getLucideIcon(card.groupIcon) : null, cards: [] };
      byLabel.set(label, group);
    }
    group.cards.push(card);
  }

  return [...byLabel.values()].sort((a, b) => {
    if (a.label === UNGROUPED_KPI_LABEL) return 1;
    if (b.label === UNGROUPED_KPI_LABEL) return -1;
    return 0;
  });
}

/** Whether this group's cards are visible (ungrouped uses `showAll`). */
export function isKpiGroupExpanded(
  label: string,
  grouped: boolean,
  expandedGroups: Record<string, boolean>,
  showAll: boolean,
): boolean {
  return grouped ? !!expandedGroups[label] : showAll;
}

/** Toggle one group, or `showAll` when cards are not grouped. */
export function toggleKpiGroupExpanded(
  grouped: boolean,
  label: string,
  showAll: boolean,
  expandedGroups: Record<string, boolean>,
): { showAll: boolean; expandedGroups: Record<string, boolean> } {
  if (!grouped) {
    return { showAll: !showAll, expandedGroups };
  }
  return { showAll, expandedGroups: { ...expandedGroups, [label]: !expandedGroups[label] } };
}

/** Cards rendered for a group (collapsed slice in view mode). */
export function visibleCardsForGroup(
  group: KpiGroup,
  editMode: boolean,
  expanded: boolean,
): KpiCardData[] {
  if (editMode || expanded) return group.cards;
  return group.cards.slice(0, COLLAPSED_KPI_CARD_COUNT);
}

/** True when the group can show a "see more" control. */
export function isKpiGroupCollapsible(group: KpiGroup, editMode: boolean): boolean {
  return !editMode && group.cards.length > COLLAPSED_KPI_CARD_COUNT;
}

/** Reorder within the same family; cross-group drops are ignored. */
export function applyKpiDragEnd(
  items: KpiCardData[],
  activeId: string,
  overId: string,
  grouped: boolean,
): KpiCardData[] {
  const groupOf = (id: string) => items.find((item) => item.id === id)?.groupLabel ?? null;
  if (grouped && groupOf(activeId) !== groupOf(overId)) return items;

  const oldIndex = items.findIndex((item) => item.id === activeId);
  const newIndex = items.findIndex((item) => item.id === overId);
  if (oldIndex < 0 || newIndex < 0) return items;
  return arrayMove(items, oldIndex, newIndex);
}

/** Error, empty, loading, or ready — the grid picks the panel from this. */
export function resolveKpiLoadStatus(params: ResolveKpiLoadStatusParams): KpiLoadStatus {
  const showSkeleton = params.isLoadingElements || (params.isLoadingKpis && !params.response);
  const hasLoadedEmpty =
    !params.isLoadingKpis &&
    !params.isKpiError &&
    params.response != null &&
    Object.keys(params.response.stats ?? {}).length === 0;

  if (params.isKpiError) return 'error';
  if (hasLoadedEmpty) return 'empty';
  if (showSkeleton) return 'loading';
  return 'ready';
}

/** Which dashboard IE filters the KPI toolbar should show. */
export function resolveKpiFilterVisibility(elements: { elementId: string }[]): KpiFilterVisibility {
  const showPeriodFilter = dashboardHasElement(elements, KPI_FILTER_ELEMENT_IDS.period);
  const showDateRangeFilter = dashboardHasElement(elements, KPI_FILTER_ELEMENT_IDS.dateRange);
  const showComparisonFilter = dashboardHasElement(elements, KPI_FILTER_ELEMENT_IDS.comparison);
  const showEstablishmentFilter = dashboardHasElement(
    elements,
    KPI_FILTER_ELEMENT_IDS.establishment,
  );
  return {
    showPeriodFilter,
    showDateRangeFilter,
    showComparisonFilter,
    showEstablishmentFilter,
    hasAnyFilter:
      showPeriodFilter || showDateRangeFilter || showComparisonFilter || showEstablishmentFilter,
  };
}

/** Local calendar date as `YYYY-MM-DD` for {@link DateRangeInput}. */
export function toKpiFilterDateString(d?: Date): string | undefined {
  return d ? toLocalDateString(d) : undefined;
}

/** Custom end date: parse local end-of-day. */
function parseKpiFilterEndDate(val: string): Date {
  const d = parseLocalDateString(val);
  d.setHours(23, 59, 59);
  return d;
}

/** Preset period: drop custom dates and comparison. */
export function applyKpiPeriod(period: DashboardStatsPeriod): DashboardStatsDateFilterState {
  return {
    dateRange: period,
    customDateFrom: undefined,
    customDateTo: undefined,
    comparisonPeriod: 'none',
  };
}

/** Custom start date: parse local midnight and drop comparison. */
export function applyKpiCustomFrom(
  filters: DashboardStatsDateFilterState,
  val: string,
): DashboardStatsDateFilterState {
  if (!val) {
    return { ...filters, customDateFrom: undefined };
  }
  return {
    ...filters,
    dateRange: 'all',
    customDateFrom: parseLocalDateString(val),
    comparisonPeriod: 'none',
  };
}

/** Custom end date: parse local end-of-day and drop comparison. */
export function applyKpiCustomTo(
  filters: DashboardStatsDateFilterState,
  val: string,
): DashboardStatsDateFilterState {
  if (!val) {
    return { ...filters, customDateTo: undefined };
  }
  return {
    ...filters,
    dateRange: 'all',
    customDateTo: parseKpiFilterEndDate(val),
    comparisonPeriod: 'none',
  };
}

/** Comparison chip: drop preset / custom dates. */
export function applyKpiComparison(
  comparisonPeriod: ComparisonPeriod,
): DashboardStatsDateFilterState {
  return {
    dateRange: 'all',
    customDateFrom: undefined,
    customDateTo: undefined,
    comparisonPeriod,
  };
}

/** Single-establishment scope (empty value clears the filter). */
export function applyKpiEstablishment(
  filters: DashboardStatsDateFilterState,
  val: string,
): DashboardStatsDateFilterState {
  return {
    ...filters,
    establishmentIds: val ? [val] : undefined,
  };
}

/** Props {@link KpiCard} needs from a mapped KPI row. */
export function toKpiCardProps(kpi: KpiCardData) {
  return {
    title: kpi.title,
    value: kpi.value,
    icon: kpi.icon,
    iconColor: kpi.iconColor,
    iconBgColor: kpi.iconBgColor,
    titleColor: kpi.titleColor,
    trend: kpi.trend,
    description: kpi.description,
    tooltip: kpi.tooltip,
    comparisonLabel: kpi.comparisonLabel,
  };
}
